import { portalLocale, portalText } from "../../i18n/portal.js";
/**
 * Area `billing-card` of the console gateway — owned by plan-002 unit **RB-23** (design §15.6.3,
 * amended 2026-09-23).
 *
 * **A card is added by buying credit** (product decision 2026-09-23, option (b)). EveryPay
 * documents no card-saving mode without a payment: its Payform needs an amount, and the mandate
 * an automatic top-up charges is issued only by a real first payment made from a form opened with
 * `frequencyType: 'recurring'`. So adding a card is two calls with the processor's own form in
 * between:
 *
 *   1. `startHostedCardForm(creditPackCode)` → `createHostedCardForm`: the server answers with
 *      a quote of the pack's net + VAT (its own price rule; FIX-MONEY C1 — net, VAT, total and
 *      `billing_quote_id`), the processor's public key, the processor's script and the frequency type.
 *   2. {@link openPayform} mounts EveryPay's form with exactly that, inside the page. The card is
 *      typed into EveryPay's iframe — never into WeTalk's DOM — and the form hands back a
 *      single-use `ctn_` token.
 *   3. `completeCardPurchase({ creditPackCode, cardToken, billingQuoteId, amountDue })` → the server
 *      confirms the quote the form was opened for (refusing a form amount that is not its total),
 *      charges the token, credits the net and stores the card from EveryPay's answer. The console
 *      sends no stored token.
 *
 * The `Idempotency-Key` the purchase requires is derived from the single-use token, so a retry
 * of the SAME purchase replays the first answer, and a new purchase can never collide with it.
 *
 * ── HOW THIS FILE WORKS (IC-2, created by RB-03) ─────────────────────────────────────────
 * The method signatures below are merged onto `ConsoleGateway` by declaration merging, so
 * RB-23 owns them here. `billingCardGap` is empty: both operations are documented.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";
import { money, type CurrencyCode, type Money } from "@wetalk/domain";

import type {
  ChargeAttemptState,
  ConsoleGateway,
  HostedCardFormView,
  PaymentMethodView,
  TopUpOutcomeView,
} from "../gateway.js";
import { readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** The first purchase: which pack, and the token the processor's form produced. */
export interface CardPurchaseRequestView {
  readonly creditPackCode: string;
  /** FIX-MONEY C1: the quote the card form was opened for, and the total (net + VAT) it opened with. */
  readonly billingQuoteId: string;
  readonly amountDue: Money;
  /** The Payform's single-use `ctn_` token. Never a card number, never a stored token. */
  readonly cardToken: string;
}

/** What the first purchase did — a top-up outcome, plus the card it kept. */
export interface CardPurchaseOutcomeView extends TopUpOutcomeView {
  /** True when the charge's outcome was unknown and the server looked it up by reference. */
  readonly lookedUp: boolean;
  /** The card stored from the processor's answer, or null. */
  readonly paymentMethod: PaymentMethodView | null;
  /** Why a successful purchase kept no card. The credit was still added. */
  readonly cardNotSavedReason: string | null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** What to open EveryPay's Payform with, for this credit pack. The console never renders a card field. */
    startHostedCardForm(creditPackCode: string): Promise<HostedCardFormView>;
    /** Buys the pack with the form's token and keeps the card EveryPay describes. */
    completeCardPurchase(request: CardPurchaseRequestView): Promise<CardPurchaseOutcomeView>;
  }
}

export const billingCardGap: readonly GatewayGap[] = [];

const CHARGE_STATE: readonly ChargeAttemptState[] = [
  "succeeded",
  "declined",
  "requires_action",
  "requires_reauth",
];

export function billingCardApiSlice(
  client: WeTalkApiClient,
  _context: SliceContext,
): Pick<ConsoleGateway, "startHostedCardForm" | "completeCardPurchase"> {
  return {
    async startHostedCardForm(creditPackCode: string): Promise<HostedCardFormView> {
      const data = readRecord(
        readRecord(
          assertOk(await client.createHostedCardForm({ body: { credit_pack_code: creditPackCode } })),
          "createHostedCardForm",
        )["data"],
        "createHostedCardForm.data",
      );
      const currency = readText(data["currency"], "hostedCardForm.currency") as CurrencyCode;
      const frequencyType = readText(data["frequency_type"], "hostedCardForm.frequency_type");
      if (frequencyType !== "recurring") {
        throw new Error(`"${frequencyType}" is not the documented frequency type for a card-adding purchase.`);
      }
      const amountMinor = readWholeNumber(data["amount_minor"], "hostedCardForm.amount_minor");
      const netMinor = readWholeNumber(data["net_minor"], "hostedCardForm.net_minor");
      const taxMinor = readWholeNumber(data["tax_minor"], "hostedCardForm.tax_minor");
      if (netMinor + taxMinor !== amountMinor) {
        throw new Error("The card form's total is not its credit plus VAT; refusing to open the payment form.");
      }
      return {
        creditPackCode: readText(data["credit_pack_code"], "hostedCardForm.credit_pack_code"),
        // FIX-MONEY C1: the form opens for net + VAT — the figure the card is charged.
        amount: money(amountMinor, currency),
        billingQuoteId: readText(data["billing_quote_id"], "hostedCardForm.billing_quote_id"),
        net: money(netMinor, currency),
        tax: money(taxMinor, currency),
        taxRatePermille: readWholeNumber(data["tax_rate_permille"], "hostedCardForm.tax_rate_permille"),
        publicKey: readText(data["public_key"], "hostedCardForm.public_key"),
        scriptUrl: readText(data["script_url"], "hostedCardForm.script_url"),
        frequencyType,
      };
    },

    async completeCardPurchase(request: CardPurchaseRequestView): Promise<CardPurchaseOutcomeView> {
      const data = readRecord(
        readRecord(
          assertOk(
            await client.completeCardPurchase({
              body: {
                credit_pack_code: request.creditPackCode,
                card_token: request.cardToken,
                billing_quote_id: request.billingQuoteId,
                amount_due_minor: request.amountDue.amountMinor,
              },
              header: { "Idempotency-Key": `card-purchase-${request.cardToken}` },
            }),
          ),
          "completeCardPurchase",
        )["data"],
        "completeCardPurchase.data",
      );
      const currency = readText(data["currency"], "cardPurchase.currency") as CurrencyCode;
      const status = readText(data["status"], "cardPurchase.status");
      const balanceAfter = data["balance_after_minor"];
      const stored = data["payment_method"];
      return {
        chargeAttemptId: readText(data["charge_attempt_id"], "cardPurchase.charge_attempt_id"),
        status: status === "error" ? "error" : readPurchaseState(status),
        amount: money(readWholeNumber(data["amount_minor"], "cardPurchase.amount_minor"), currency),
        balanceAfter:
          balanceAfter === null
            ? null
            : money(readWholeNumber(balanceAfter, "cardPurchase.balance_after_minor"), currency),
        redirectUrl: typeof data["redirect_url"] === "string" ? data["redirect_url"] : null,
        declineCode: typeof data["decline_code"] === "string" ? data["decline_code"] : null,
        declineMessage: typeof data["decline_message"] === "string" ? data["decline_message"] : null,
        // UNKNOWN, looked up once, now the poller's. It means "wait", never "try again".
        pendingReconciliation: data["resolution"] === "pending_reconciliation",
        lookedUp: readBoolean(data["looked_up"], "cardPurchase.looked_up"),
        paymentMethod: stored === null ? null : paymentMethodOf(stored),
        cardNotSavedReason:
          typeof data["card_not_saved_reason"] === "string" ? data["card_not_saved_reason"] : null,
      };
    },
  };
}

function readPurchaseState(value: string): ChargeAttemptState {
  const found = CHARGE_STATE.find((one) => one === value);
  if (found === undefined) {
    throw new Error(`"${value}" is not a documented card-purchase status.`);
  }
  return found;
}

function paymentMethodOf(value: unknown): PaymentMethodView {
  const each = readRecord(value, "cardPurchase.payment_method");
  return {
    paymentMethodId: readText(each["payment_method_id"], "cardPurchase.payment_method.payment_method_id"),
    brand: readText(each["brand"], "cardPurchase.payment_method.brand"),
    lastFour: readText(each["last_four"], "cardPurchase.payment_method.last_four"),
    expiryMonth: readWholeNumber(each["expiry_month"], "cardPurchase.payment_method.expiry_month"),
    expiryYear: readWholeNumber(each["expiry_year"], "cardPurchase.payment_method.expiry_year"),
    holderName: readText(each["holder_name"], "cardPurchase.payment_method.holder_name"),
    isDefault: readBoolean(each["is_default"], "cardPurchase.payment_method.is_default"),
    hasMandate: readBoolean(each["has_mandate"], "cardPurchase.payment_method.has_mandate"),
  };
}

// ---------------------------------------------------------------------------------------
// The processor's form, mounted
// ---------------------------------------------------------------------------------------

/** The only two scripts EveryPay documents. Anything else is refused before it is loaded. */
const PAYFORM_SCRIPT = /^https:\/\/(sandbox-)?js\.everypay\.gr\/v3$/;

/** The part of `window.everypay` this console uses (accept-payments/payform-integration). */
interface EveryPayScript {
  payform(payload: Readonly<Record<string, unknown>>, respond: (answer: PayformAnswer) => void): void;
}

interface PayformAnswer {
  readonly onLoad?: boolean;
  readonly response?: "success" | "error";
  readonly token?: string;
  readonly msg?: { readonly message?: string };
}

/**
 * Mounts EveryPay's Payform into the element `elementId` and resolves with the `ctn_` token.
 *
 * The payload is the server's, field for field: `pk`, `amount` and `frequencyType` come from
 * {@link HostedCardFormView} and nothing is computed here. `txnType: 'tds'` is the documented
 * normal flow (3-D Secure). Rejects with the processor's own message when the form reports an
 * error, and when the script cannot be loaded.
 */
export async function openPayform(form: HostedCardFormView, elementId: string): Promise<string> {
  const everypay = await payformScriptOf(form.scriptUrl);
  return new Promise<string>((resolve, reject) => {
    everypay.payform(
      {
        pk: form.publicKey,
        amount: form.amount.amountMinor,
        txnType: "tds",
        frequencyType: form.frequencyType,
        elementId,
        locale: portalLocale(),
      },
      (answer) => {
        if (answer.response === "success" && typeof answer.token === "string") {
          resolve(answer.token);
        } else if (answer.response === "error") {
          reject(new Error(answer.msg?.message ?? portalText("shell", "payment.formError")));
        }
        // `onLoad` needs nothing: the form is visible once the iframe has loaded.
      },
    );
  });
}

async function payformScriptOf(scriptUrl: string): Promise<EveryPayScript> {
  if (!PAYFORM_SCRIPT.test(scriptUrl)) {
    throw new Error(`Refusing to load "${scriptUrl}": it is not one of EveryPay's documented Payform scripts.`);
  }
  const loaded = (globalThis as { everypay?: EveryPayScript }).everypay;
  if (loaded !== undefined) return loaded;
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.addEventListener("load", () => {
      resolve();
    });
    script.addEventListener("error", () => {
      reject(new Error("The payment form could not be loaded from the processor."));
    });
    document.head.append(script);
  });
  const mounted = (globalThis as { everypay?: EveryPayScript }).everypay;
  if (mounted === undefined) {
    throw new Error("The processor's script loaded but did not provide its payment form.");
  }
  return mounted;
}
